import { useCustomTranslation } from './useCustomTranslation'
import { LabeledOption } from '../Types/globalTypes'
import { useMemo } from 'react'
import countryTranslations from 'i18n-iso-countries'

import { countries as countriesData } from 'country-data-list'

export enum CountryCodes {
  AUSTRIA = 'AUT',
  BELGIUM = 'BEL',
  SWITZERLAND = 'CHE',
  GERMANY = 'DEU',
  DENMARK = 'DNK',
  SPAIN = 'ESP',
  FINLAND = 'FIN',
  FRANCE = 'FRA',
  HUNGARY = 'HUN',
  ITALY = 'ITA',
  LICHTENSTEIN = 'LIE',
  LUXEMBOURG = 'LUX',
  NETHERLANDS = 'NLD',
  NORWAY = 'NOR',
  POLAND = 'POL',
  PORTUGAL = 'PRT',
  SWEDEN = 'SWE',
  CZECHIA = 'CZE',
  GREECE = 'GRC',
}
export type CountryCode = `${CountryCodes}`

/* The interface came from 'country-data-list' lib. */
interface Country {
  alpha2: string
  alpha3: string
  countryCallingCodes?: string[]
  currencies: string[]
  emoji?: string
  ioc?: string
  languages: string[]
  name: string
  status?: string
}

/* `country-data-list` only types `all`, but the runtime object is also keyed by ISO alpha-3 code. */
const countries = countriesData as Record<string, unknown> & { all: Country[] }

interface UseCountryOptionsReturnType {
  allCountries: LabeledOption<string>[]
  supportedCountries: LabeledOption<CountryCodes>[]
  supportedE3dcInstallationCountries: LabeledOption<CountryCodes>[]
  supportedFlowInstallationCountries: LabeledOption<CountryCodes>[]
}

export const useCountryOptions = (): UseCountryOptionsReturnType => {
  const { selectedAppLanguage } = useCustomTranslation()

  /* e3dc installations has support for this countries. same list maintained in backend. any change requires backend update */
  const supportedE3dcInstallationCountries: LabeledOption<CountryCodes>[] = useMemo(() => {
    return [
      {
        value: CountryCodes.AUSTRIA,
        label: countryTranslations.getName(CountryCodes.AUSTRIA, selectedAppLanguage) || (countries[CountryCodes.AUSTRIA] as Country).name,
      },
      {
        value: CountryCodes.SWITZERLAND,
        label: countryTranslations.getName(CountryCodes.SWITZERLAND, selectedAppLanguage) || (countries[CountryCodes.SWITZERLAND] as Country).name,
      },
      {
        value: CountryCodes.GERMANY,
        label: countryTranslations.getName(CountryCodes.GERMANY, selectedAppLanguage) || (countries[CountryCodes.GERMANY] as Country).name,
      },
      {
        value: CountryCodes.SPAIN,
        label: countryTranslations.getName(CountryCodes.SPAIN, selectedAppLanguage) || (countries[CountryCodes.SPAIN] as Country).name,
      },
      {
        value: CountryCodes.NETHERLANDS,
        label: countryTranslations.getName(CountryCodes.NETHERLANDS, selectedAppLanguage) || (countries[CountryCodes.NETHERLANDS] as Country).name,
      },
    ].sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))
  }, [selectedAppLanguage])

  /* Flow installations has support for this countries. same list maintained in backend. any change requires backend update */
  const supportedFlowInstallationCountries: LabeledOption<CountryCodes>[] = useMemo(() => {
    return [
      ...supportedE3dcInstallationCountries,
      {
        value: CountryCodes.FRANCE,
        label: countryTranslations.getName(CountryCodes.FRANCE, selectedAppLanguage) || (countries[CountryCodes.FRANCE] as Country).name,
      },
      {
        value: CountryCodes.POLAND,
        label: countryTranslations.getName(CountryCodes.POLAND, selectedAppLanguage) || (countries[CountryCodes.POLAND] as Country).name,
      },
      {
        value: CountryCodes.PORTUGAL,
        label: countryTranslations.getName(CountryCodes.PORTUGAL, selectedAppLanguage) || (countries[CountryCodes.PORTUGAL] as Country).name,
      },
      {
        value: CountryCodes.ITALY,
        label: countryTranslations.getName(CountryCodes.ITALY, selectedAppLanguage) || (countries[CountryCodes.ITALY] as Country).name,
      },
      {
        value: CountryCodes.SWEDEN,
        label: countryTranslations.getName(CountryCodes.SWEDEN, selectedAppLanguage) || (countries[CountryCodes.SWEDEN] as Country).name,
      },
      {
        value: CountryCodes.CZECHIA,
        label: countryTranslations.getName(CountryCodes.CZECHIA, selectedAppLanguage) || (countries[CountryCodes.CZECHIA] as Country).name,
      },
      {
        value: CountryCodes.BELGIUM,
        label: countryTranslations.getName(CountryCodes.BELGIUM, selectedAppLanguage) || (countries[CountryCodes.BELGIUM] as Country).name,
      },
      {
        value: CountryCodes.LUXEMBOURG,
        label: countryTranslations.getName(CountryCodes.LUXEMBOURG, selectedAppLanguage) || (countries[CountryCodes.LUXEMBOURG] as Country).name,
      },
      {
        value: CountryCodes.GREECE,
        label: countryTranslations.getName(CountryCodes.GREECE, selectedAppLanguage) || (countries[CountryCodes.GREECE] as Country).name,
      },
    ].sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))
  }, [selectedAppLanguage, supportedE3dcInstallationCountries])

  const supportedCountries: LabeledOption<CountryCodes>[] = useMemo(() => {
    return [
      {
        value: CountryCodes.AUSTRIA,
        label: countryTranslations.getName(CountryCodes.AUSTRIA, selectedAppLanguage) || (countries[CountryCodes.AUSTRIA] as Country).name,
      },
      {
        value: CountryCodes.BELGIUM,
        label: countryTranslations.getName(CountryCodes.BELGIUM, selectedAppLanguage) || (countries[CountryCodes.BELGIUM] as Country).name,
      },
      {
        value: CountryCodes.SWITZERLAND,
        label: countryTranslations.getName(CountryCodes.SWITZERLAND, selectedAppLanguage) || (countries[CountryCodes.SWITZERLAND] as Country).name,
      },
      {
        value: CountryCodes.GERMANY,
        label: countryTranslations.getName(CountryCodes.GERMANY, selectedAppLanguage) || (countries[CountryCodes.GERMANY] as Country).name,
      },
      {
        value: CountryCodes.DENMARK,
        label: countryTranslations.getName(CountryCodes.DENMARK, selectedAppLanguage) || (countries[CountryCodes.DENMARK] as Country).name,
      },
      {
        value: CountryCodes.SPAIN,
        label: countryTranslations.getName(CountryCodes.SPAIN, selectedAppLanguage) || (countries[CountryCodes.SPAIN] as Country).name,
      },
      {
        value: CountryCodes.FINLAND,
        label: countryTranslations.getName(CountryCodes.FINLAND, selectedAppLanguage) || (countries[CountryCodes.FINLAND] as Country).name,
      },
      {
        value: CountryCodes.FRANCE,
        label: countryTranslations.getName(CountryCodes.FRANCE, selectedAppLanguage) || (countries[CountryCodes.FRANCE] as Country).name,
      },
      {
        value: CountryCodes.HUNGARY,
        label: countryTranslations.getName(CountryCodes.HUNGARY, selectedAppLanguage) || (countries[CountryCodes.HUNGARY] as Country).name,
      },
      {
        value: CountryCodes.ITALY,
        label: countryTranslations.getName(CountryCodes.ITALY, selectedAppLanguage) || (countries[CountryCodes.ITALY] as Country).name,
      },
      {
        value: CountryCodes.LICHTENSTEIN,
        label: countryTranslations.getName(CountryCodes.LICHTENSTEIN, selectedAppLanguage) || (countries[CountryCodes.LICHTENSTEIN] as Country).name,
      },
      {
        value: CountryCodes.LUXEMBOURG,
        label: countryTranslations.getName(CountryCodes.LUXEMBOURG, selectedAppLanguage) || (countries[CountryCodes.LUXEMBOURG] as Country).name,
      },
      {
        value: CountryCodes.NETHERLANDS,
        label: countryTranslations.getName(CountryCodes.NETHERLANDS, selectedAppLanguage) || (countries[CountryCodes.NETHERLANDS] as Country).name,
      },
      {
        value: CountryCodes.NORWAY,
        label: countryTranslations.getName(CountryCodes.NORWAY, selectedAppLanguage) || (countries[CountryCodes.NORWAY] as Country).name,
      },
      {
        value: CountryCodes.POLAND,
        label: countryTranslations.getName(CountryCodes.POLAND, selectedAppLanguage) || (countries[CountryCodes.POLAND] as Country).name,
      },
      {
        value: CountryCodes.PORTUGAL,
        label: countryTranslations.getName(CountryCodes.PORTUGAL, selectedAppLanguage) || (countries[CountryCodes.PORTUGAL] as Country).name,
      },
      {
        value: CountryCodes.SWEDEN,
        label: countryTranslations.getName(CountryCodes.SWEDEN, selectedAppLanguage) || (countries[CountryCodes.SWEDEN] as Country).name,
      },
    ].sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))
  }, [selectedAppLanguage])

  const allCountries: LabeledOption<string>[] = useMemo(() => {
    return countries.all
      .filter((country: Country) => country.status === 'assigned')
      .map((country: Country) => {
        return {
          value: country.alpha3,
          label: countryTranslations.getName(country.alpha3, selectedAppLanguage) || country.name,
        }
      })
      .filter((option: LabeledOption<string>) => !!option.label)
      .sort((a: LabeledOption<string>, b: LabeledOption<string>) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))
  }, [selectedAppLanguage])

  return { allCountries, supportedCountries, supportedE3dcInstallationCountries, supportedFlowInstallationCountries }
}

export const useCountryCodesValidation = (): [(countryCode: string) => boolean] => {
  const { supportedCountries } = useCountryOptions()

  const validateCountryCode = (countryCode: string): boolean => {
    return supportedCountries.map(elem => elem.value).includes(countryCode as CountryCodes)
  }

  return [validateCountryCode]
}

export const useUstIdCountries = (): LabeledOption<string>[] => {
  return [
    { value: 'AT', label: 'AT' },
    { value: 'BE', label: 'BE' },
    { value: 'CY', label: 'CY' },
    { value: 'CZ', label: 'CZ' },
    { value: 'DE', label: 'DE' },
    { value: 'DK', label: 'DK' },
    { value: 'EE', label: 'EE' },
    { value: 'EL', label: 'EL' },
    { value: 'ES', label: 'ES' },
    { value: 'FI', label: 'FI' },
    { value: 'FR', label: 'FR' },
    { value: 'HR', label: 'HR' },
    { value: 'HU', label: 'HU' },
    { value: 'IE', label: 'IE' },
    { value: 'IT', label: 'IT' },
    { value: 'LT', label: 'LT' },
    { value: 'LU', label: 'LU' },
    { value: 'LV', label: 'LV' },
    { value: 'MT', label: 'MT' },
    { value: 'NL', label: 'NL' },
    { value: 'PL', label: 'PL' },
    { value: 'PT', label: 'PT' },
    { value: 'RO', label: 'RO' },
    { value: 'SE', label: 'SE' },
    { value: 'SI', label: 'SI' },
    { value: 'SK', label: 'SK' },
    { value: 'XI', label: 'XI' },
  ]
}
